/**
 * Register events for webapp
 *
 * @package ARS Webapp
 * @author René Schulze <schulze@thadeus-roth.de>
 */

import { routeTo, getStationIdForRecommendation, getUnseenStories, getNearestStation } from '?/Composable/Tools';
import { inject, onMounted } from 'vue';
import { useStore } from 'vuex';
import config from '?/Config';
import constants from '?/Constants';
import emitter from '?/Composable/Events/Emitter';
import repository from '?/Composable/Repository';
import geolocation from '?/Composable/Geolocation';
import TextModal from '?/Component/ModalContent/Text';
import { t } from '?/Composable/Translation';
import { useRoute } from 'vue-router';

export default function registerEvents() {
    const store = useStore();
    const route = useRoute();

    /*
     * Route to home
     */
    emitter.on('*', (e, authToken) => {
        if (['login-is-authenticated'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Route to home'
                );
            }

            // Search for query parameter `target` or use `/webapp/` as fallback
            let target = decodeURI(
                (new URL(document.location)).searchParams.get('target') ||
                '/webapp/'
            );

            routeTo(target);
        }
    });

    /*
     * Store authentication state
     */
    emitter.on('*', (e, authToken) => {
        if (['user-is-authenticated', 'login-is-authenticated'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Store authentication state'
                );
            }

            store.commit('authenticated', repository.isAuthenticated());
        }
    });

    /*
     * Establish SSE connection
     */
    emitter.on('*', (e, authToken) => {
        if (['user-is-authenticated', 'login-is-authenticated'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Etablish SSE connection'
                );
            }

            repository.connect();

            // Reconnect after disconnect
            document.addEventListener("visibilitychange", () => {
                if (document.hidden || !repository.isAuthenticated()) {
                    return;
                }

                store.commit('flashMessageReload', null);
                store.commit('flashMessage', null);

                if (repository.getEventStream().readyState === 2) {
                    repository.connect();

                    if (config.debug) {
                        console.log(
                            'Event: `visibilitychange`',
                            'Action: ', 'Reconnect SSE connection'
                        );
                    }
                }
            });
        }
    });

    /*
     * Check geolocation
     */
    emitter.on('*', (e, authToken) => {
        if (['user-is-authenticated', 'login-is-authenticated'].includes(e)) {
            if (constants.allowGeolocation === false) {
                return;
            }

            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Check geolocation'
                );
            }

            geolocation();
        }
    });

    /*
     * Ask for notification permission
     */
    emitter.on('*', (e, authToken) => {
        if (['user-is-authenticated', 'login-is-authenticated'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Ask for notification permission'
                );
            }

            let requestPermissionInProgress = false;

            async function requestNotificationPermission() {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    // You can now use the Badging API
                }
            }

            async function checkNotificationPermission() {
                if (requestPermissionInProgress) {
                    return;
                }

                requestPermissionInProgress = true;

                const permissionStatus = await navigator
                    .permissions
                    .query({ name: 'notifications' });

                switch (permissionStatus.state) {
                    case 'granted':
                      // You can use the Badging API
                      break;
                    case 'denied':
                      // The user has denied the permission
                      break;
                    default:
                      // The user has not yet granted or denied the permission
                      await requestNotificationPermission();
                      break;
                }

                document.body.removeEventListener('click', checkNotificationPermission);
            }

            // Only ask for permission, if app is installed on device
            if (
                window.matchMedia('(display-mode: standalone)').matches ||
                ('standalone' in window.navigator && window.navigator.standalone)
            ) {
                document.body.addEventListener('click', checkNotificationPermission);
            }
        }
    });

    /*
     * Route to login
     */
     emitter.on('*', (e, authToken) => {
        if (['user-is-not-authenticated'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Route to login'
                );
            }

            routeTo('/webapp/login', true, true);
        }
    });

    /*
     * Handle recommendations
     */
     emitter.on('*', (e, recommendation) => {
        if (['sse-webcontentRecommendation', 'sse-webtextRecommendation'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Handle recommendations'
                );
            }

            const stationId = getStationIdForRecommendation();

            if (stationId) {
                store.dispatch(
                    'addRecommendationDelayed',
                    {
                        message: recommendation,
                        stationId: stationId,
                        delay: constants.defaultUnreadMessageDelay
                    }
                );
            } else {
                store.commit('addNewsMessage', recommendation);
            }
        }
    });

    /*
     * Check for unseen stories
     */
     emitter.on('*', (e, recommendation) => {
        if (['sse-playerstatus'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Check for unseen stories'
                );
            }

            const unseenStories = getUnseenStories();

            if (unseenStories.length) {
                store.commit('unseenNews', true);
            }
        }
    });

    /*
     * Check for stations nearby for news notification
     */
     emitter.on('*', (e, position) => {
        if (['geolocation-success'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Check stations nearby for news notification'
                );
            }

            if (getNearestStation()) {
                store.commit('unseenNews', true);
            }
        }
    });

    /*
     * Check for change of unread message count
     */
     emitter.on('*', (e, recommendation) => {
        if (['unread-message-added', 'unread-message-count-changed'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Check for change of unread message count'
                );
            }

            const unseenMessages = store.getters.unreadMessages.length;

            if (unseenMessages > 0) {
                if ('setAppBadge' in navigator) {
                    navigator.setAppBadge(unseenMessages).catch((error) => {
                        console.log('Unable to set unseen message count to app bagde', error);
                    });
                }
            } else {
                if ('clearAppBadge' in navigator) {
                    navigator.clearAppBadge().catch((error) => {
                        console.log('Unable to clear app bagde', error);
                    });
                }
            }
        }
    });

    /*
     * Open team invitation modal
     */
    emitter.on('*', (e, authToken) => {
        if (['user-is-authenticated', 'login-is-authenticated'].includes(e)) {
            if (config.debug) {
                console.log(
                    'Webapp event: ', e,
                    'Action: ', 'Check for team invitation modal'
                );
            }

            const token = (new URL(document.location)).searchParams.get('tjr');

            if (token) {
                repository.joinTeam(token)
                    .then((response) => {
                        // @event team-joined
                        emitter.emit('team-joined', token);

                        // @event activate-modal
                        emitter.emit('activate-modal', {
                            component: TextModal,
                            title: t('team.invite-successful'),
                            data: {
                                text: t('team.invite-successful-description')
                            }
                        });
                    })
                    .catch((error) => {
                        store.commit('flashMessage', t('apiError.team.' + error.code));

                        throw error;
                    });
            }
        }
    });

    /*
     * Route to team view if needed
     */
    emitter.on('*', (e, authToken) => {
        if (['sse-playerstatus', 'router-change-view'].includes(e)) {
            if (
                config.allowTeams &&
                store.getters.hasTeam === false &&
                !route.meta.rules.noTeam
            ) {
                routeTo('/webapp/team/');

                if (config.debug) {
                    console.log(
                        'Webapp event: ', e,
                        'Action: ', 'Routed to team view'
                    );
                }
            }
        }
    });
};
